import type {
  CongregationAdminApplicationsResponse,
  CongregationAdminAssignInput,
  CongregationAdminOverrideInput,
  CongregationAdminPipelineResponse,
  CongregationAdminActivityResponse,
  CongregationApplication,
  CongregationApplicationDetail,
  CongregationApplicationStatus,
  CongregationAnniversaryReport,
  CongregationBirthdayReport,
  CongregationBatch,
  CongregationBatchInput,
  CongregationBatchStatus,
  CongregationBatchEnrollment,
  CongregationBatchEnrollmentInput,
  CongregationBatchEnrollmentStatus,
  CongregationBatchPost,
  CongregationBatchPostInput,
  CongregationBatchPostWithDetails,
  CongregationBatchPostReaction,
  CongregationBatchPostReply,
  CongregationBatchPostReplyInput,
  CongregationBatchPostAttachment,
  CongregationBatchPostPoll,
  CongregationBatchPostPollOption,
  CongregationBatchPostPollVote,
  BatchPostReactionType,
  CongregationBookInput,
  CongregationCenterInput,
  CongregationChantingInput,
  CongregationCourseInput,
  CongregationCourseVerification,
  CongregationMastersPayload,
  CongregationMember,
  CongregationMembership,
  CongregationMentorDevoteeCreate,
  CongregationMemberProfile,
  CongregationMemberDocument,
  CongregationMemberCourse,
  CongregationMemberSearchResult,
  CongregationSkillInput,
  CongregationSpiritualGuideInput,
  CongregationSpiritualMasterInput,
  CongregationDocumentVerification,
  CongregationChantingRecord,
  CongregationRole,
  CongregationRoleAssignment,
  CongregationRoleAssignmentInput,
  CongregationHousehold,
  CongregationHouseholdLabel,
  CongregationHouseholdLabelAssignmentInput,
  CongregationHouseholdLabelInput,
  CongregationHouseholdRosterEntry,
  CongregationHouseholdMember,
  CongregationHouseholdInput,
  CongregationHouseholdMemberInput,
  CongregationHouseholdMemberUpdateInput,
  CongregationHouseholdHeadInput,
  CongregationEligibilityRule,
  CongregationEligibilityRuleInput,
  CongregationEligibilityEvaluation,
  CongregationEligibilitySummary,
  CongregationMentorCohortSummary,
  CongregationChantingProgressRow,
  CongregationCourseHeatmapRow,
  CongregationImportJob,
  CongregationImportJobRow,
  CongregationImportRequest,
  CongregationBackupRun,
  CongregationBackupRequest,
  CongregationBackupUpdate,
  CongregationMonitoringAlert,
  CongregationAlertRequest,
  CongregationAccessReview,
  CongregationAccessReviewRequest,
} from '../types/congregation';
import type {
  SadhanaAssignmentResponse,
  SadhanaAssignmentsResponse,
  SadhanaCurrentAssignmentResponse,
  SadhanaTemplateAssignmentRequest,
  SadhanaTemplateListResponse,
  SadhanaTemplateResponse,
  SadhanaWeekDetailResponse,
  SadhanaWeekListResponse,
  SadhanaWeekSubmitResponse,
  SadhanaWeekMentorCommentRequest,
  SadhanaWeekMentorCommentResponse,
  SadhanaMigrationStatusResponse,
  SadhanaMigrationRunListResponse,
  SadhanaReseedRequest,
  SadhanaReseedResponse,
  ChantingPrescriptionCreateRequest,
  ChantingPrescriptionCreateResponse,
  ChantingPrescriptionListResponse,
} from '../types/sadhana';
import type { User } from '../models/all';

export const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_CHECK_URL = process.env.REACT_APP_AUTH_CHECK_URL || `${API_BASE_URL}/api/user/me`;

export class ApiError extends Error {
  status: number;
  data?: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export async function apiFetch<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      ...(options.body instanceof FormData
        ? {}
        : { 'Content-Type': 'application/json' }),
      ...(options.headers || {}),
    },
    ...options,
  });
  if (!response.ok) {
    if (response.status === 401) {
      const redirectUrl = encodeURIComponent(window.location.href);
      window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
      throw new ApiError('Unauthorized', response.status);
    }

    let errorData: unknown = null;
    let message = 'API request failed';

    try {
      const raw = await response.text();
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          errorData = parsed;
          if (parsed && typeof parsed === 'object' && 'message' in parsed) {
            message = String((parsed as { message?: unknown }).message ?? message);
          }
        } catch {
          errorData = raw;
          message = raw;
        }
      }
    } catch {
      // ignore parsing failures and fall back to default message
    }

    throw new ApiError(message, response.status, errorData);
  }
  if (response.status === 204) {
    return null as T;
  }
  try {
    return (await response.json()) as T;
  } catch {
    const raw =
      typeof (response as any).text === 'function'
        ? await (response as any).text()
        : '';
    if (!raw) return null as T;
    const trimmed = raw.trim();
    try {
      const start = trimmed.indexOf('{');
      const slice = start > 0 ? trimmed.slice(start) : trimmed;
      return JSON.parse(slice) as T;
    } catch {
      return trimmed as unknown as T;
    }
  }
}

export async function fetchAuthCheck(init?: RequestInit) {
  return apiFetch<User>(AUTH_CHECK_URL, {
    method: init?.method ?? 'GET',
    ...init,
  });
}

export async function fetchDonations() {
  return apiFetch(`${API_BASE_URL}/api/devotee/donation`);
}


export async function fetchDonationById(id: string) {
  return apiFetch(`${API_BASE_URL}/api/devotee/donation/${id}`);
}

export async function fetchEvent(eventId: string) {
  return apiFetch(`${API_BASE_URL}/api/public/event/${eventId}`);
}

export async function fetchEventsList() {
  return apiFetch(`${API_BASE_URL}/api/public/event`);
}

export async function fetchCurrentUser() {
  return apiFetch(`${API_BASE_URL}/api/user/me`);
}

export async function createDonation(data: any) {
  return apiFetch(`${API_BASE_URL}/api/devotee/donation`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function verifyPayment(payload: any) {
  return apiFetch(`${API_BASE_URL}/api/public/payment/razorpay/verify`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateUserProfile(data: any) {
  return apiFetch(`${API_BASE_URL}/api/user/me`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}
export async function fetchUserRole() {
  return apiFetch(`${API_BASE_URL}/api/user/role`);
}

// Admin user management APIs
export interface AdminUser {
  id: number;
  firebaseUid?: string;
  userType?: string;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  pan?: string | null;
  email: string;
  birthdate?: string;
  anniversaryDate?: string;
  legalName?: string;
  displayName: string;
  photoUrl?: string;
  role?: string;
  roles?: string[];
  spiritualName?: string;
  disabled?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export async function fetchAdminUsers(query = '', limit?: number) {
  const url = new URL(
    `${API_BASE_URL}/api/admin/users${query ? '/search' : ''}`,
  );
  if (query) url.searchParams.set('q', query);
  if (limit) url.searchParams.set('limit', String(limit));
  const data = await apiFetch<(AdminUser & { role?: string })[]>(url.toString());
  return data?.map((u) => ({
    ...u,
    roles: u.roles ?? (u.role ? [u.role] : []),
  }));
}

export async function fetchAdminUser(id: number | string) {
  return apiFetch<AdminUser>(
    `${API_BASE_URL}/api/admin/users/${id}`,
  );
}

export async function updateAdminUser(
  id: number | string,
  data: Record<string, unknown>,
) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/users/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  );
}

export async function updateAdminUserLegalName(id: number | string, legalName: string) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/users/${id}/legal-name`,
    {
      method: 'PATCH',
      body: JSON.stringify({ legalName }),
    },
  );
}

export async function updateAdminUserEmail(id: number | string, email: string) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/users/${id}/email`,
    {
      method: 'PATCH',
      body: JSON.stringify({ email }),
    },
  );
}

export async function updateAdminUserPhone(id: number | string, phone: string) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/users/${id}/phone`,
    {
      method: 'PATCH',
      body: JSON.stringify({ phone }),
    },
  );
}

export async function deleteAdminUser(id: number | string) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/users/${id}`,
    {
      method: 'DELETE',
    },
  );
}

export async function updateAdminUserRoles(
  id: number | string,
  roles: string[],
) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/users/${id}/roles`,
    {
      method: 'PATCH',
      body: JSON.stringify({ roles }),
    },
  );
}

export interface AdminUserDonation {
  id: number;
  amount: number;
  date: string;
  purpose: string;
  status: string;
}

export async function fetchAdminUserDonations(id: number | string) {
  return apiFetch<AdminUserDonation[]>(
    `${API_BASE_URL}/api/admin/users/${id}/donations`,
  );
}


export interface SpiritualProfile {
  initiated: boolean;
  sikshaGuruName?: string;
  dikshaGuruName?: string;
  ashram?: string;
  mentor?: string;
  servicePreference?: string;
}

export async function fetchAdminUserSpiritualProfile(id: number | string) {
  return apiFetch<SpiritualProfile>(
    `${API_BASE_URL}/api/admin/users/${id}/spiritualprofile`,
  );
}

export async function updateAdminUserSpiritualProfile(
  id: number | string,
  data: Record<string, unknown>,
) {
  return apiFetch(`${API_BASE_URL}/api/admin/users/${id}/spiritualprofile`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export interface DonationSummary {
  totalAmount: number;
  lastDonation: string;
  currency: string;
}

export async function fetchDonationSummary(userId?: number | string) {
  const base = userId
    ? `${API_BASE_URL}/api/admin/users/${userId}/donations/summary`
    : `${API_BASE_URL}/api/user/donations/summary`;
  return apiFetch<DonationSummary>(base);
}

export interface SadhanaStats {
  averageRounds?: number | null;
}

export async function fetchAdminSadhanaStats() {
  return apiFetch<SadhanaStats>(
    `${API_BASE_URL}/api/admin/sadhana/stats`,
  );
}

// Admin announcement management APIs
export interface AdminAnnouncement {
  id: number;
  title: string;
  content: string;
  created_at?: string;
}

export interface AnnouncementStats {
  readCount: number;
  totalAnnouncements?: number;
}

export async function fetchAdminAnnouncements() {
  return apiFetch<AdminAnnouncement[]>(
    `${API_BASE_URL}/api/admin/announcements`,
  );
}

export async function searchAdminAnnouncements(q: string, limit?: number) {
  const params = new URLSearchParams({ q });
  if (limit !== undefined) params.set('limit', String(limit));
  return apiFetch<AdminAnnouncement[]>(
    `${API_BASE_URL}/api/admin/announcements/search?${params.toString()}`,
  );
}

export async function fetchAnnouncement(id: number | string) {
  return apiFetch<AdminAnnouncement>(
    `${API_BASE_URL}/api/announcements/${id}`,
  );
}

export async function fetchAdminAnnouncementStats(id?: number | string) {
  const url = id
    ? `${API_BASE_URL}/api/admin/announcements/stats?id=${id}`
    : `${API_BASE_URL}/api/admin/announcements/stats`;
  return apiFetch<AnnouncementStats>(url);
}

export async function createAdminAnnouncement(data: Record<string, unknown>) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/announcements`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
}

export async function updateAdminAnnouncement(
  id: number | string,
  data: Record<string, unknown>,
) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/announcements/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  );
}

export async function deleteAdminAnnouncement(id: number | string) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/announcements/${id}`,
    {
      method: 'DELETE',
    },
  );
}

// Admin event management APIs
export interface AdminEvent {
  id: number;
  title: string;
  poster?: string | null;
  description?: string | null;
  purpose?: string | null;
  location?: string | null;
  startDate: string;
  endDate?: string | null;
  status?: 'enabled' | 'disabled' | null;
  head?: string | null;
  hidden?: boolean | null;
  subPurposes?: {
    id?: number;
    name: string;
    amount?: number | null;
    strict: boolean;
  }[];
}

export interface AdminEventPayload {
  title: string;
  poster?: string;
  description?: string;
  purpose?: string;
  location?: string;
  startDate: string;
  endDate?: string;
  status?: 'enabled' | 'disabled';
  head?: string;
  hidden?: boolean;
  subPurposes: { name: string; amount?: number; strict: boolean }[];
}

function formatDateForApi(date: string) {
  return new Date(date).toISOString().split('.')[0] + 'Z';
}

export async function fetchAdminEvents() {
  return apiFetch<AdminEvent[]>(
    `${API_BASE_URL}/api/event/all`,
  );
}

export async function fetchAdminEvent(id: number | string) {
  return apiFetch<AdminEvent>(
    `${API_BASE_URL}/api/event/${id}`,
  );
}

export async function createAdminEvent(data: AdminEventPayload) {
  const payload = {
    ...data,
    startDate: formatDateForApi(data.startDate),
    endDate: data.endDate ? formatDateForApi(data.endDate) : undefined,
  };
  return apiFetch(
    `${API_BASE_URL}/api/event`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function updateAdminEvent(
  id: number | string,
  data: AdminEventPayload,
) {
  const payload = {
    ...data,
    startDate: formatDateForApi(data.startDate),
    endDate: data.endDate ? formatDateForApi(data.endDate) : undefined,
  };
  return apiFetch(
    `${API_BASE_URL}/api/event/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

export async function deleteAdminEvent(id: number | string) {
  return apiFetch(
    `${API_BASE_URL}/api/events/${id}`,
    {
      method: 'DELETE',
    },
  );
}

export interface EventParticipant {
  userId: number;
  fullName: string;
}

export async function fetchAdminEventParticipants(eventId: number | string) {
  const base = `${API_BASE_URL}/api/event/participants`;
  const query = `eventId=${encodeURIComponent(String(eventId))}`;
  return apiFetch<EventParticipant[]>(`${base}?${query}`);
}

// Admin seva management APIs
export interface AdminSeva {
  id: number;
  title: string;
  description?: string;
}

export async function fetchAdminSevas() {
  return apiFetch<AdminSeva[]>(
    `${API_BASE_URL}/api/admin/seva`,
  );
}

export async function createAdminSeva(data: Record<string, unknown>) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/seva`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
}

export async function updateAdminSeva(
  id: number | string,
  data: Record<string, unknown>,
) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/seva/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  );
}

export async function deleteAdminSeva(id: number | string) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/seva/${id}`,
    {
      method: 'DELETE',
    },
  );
}

export interface SevaParticipant {
  userId: number;
  fullName: string;
}

export async function fetchAdminSevaParticipants(
  sevaId: number | string,
) {
  const base = `${API_BASE_URL}/api/admin/seva/participants`;
  const query = `sevaId=${encodeURIComponent(String(sevaId))}`;
  return apiFetch<SevaParticipant[]>(`${base}?${query}`);
}

// Admin media management APIs
export interface AdminMediaItem {
  id: number;
  title: string;
  type: string;
}

export async function fetchAdminMedia() {
  return apiFetch<AdminMediaItem[]>(
    `${API_BASE_URL}/api/admin/media`,
  );
}

export async function uploadAdminMedia(
  file: File,
  folder: string,
  onProgress?: (p: number) => void,
  isPrivate: boolean = false,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    const targetFolder = isPrivate
      ? (folder.startsWith('private/') ? folder : `private/${folder}`)
      : folder;
    formData.append('folder', targetFolder);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}/api/admin/media/upload`);
    xhr.withCredentials = true;
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300 && data?.url) {
          resolve(data.url);
        } else {
          reject(new Error(data?.error || 'Upload failed'));
        }
      } catch (err) {
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(formData);
  });
}

export async function createAdminMedia(data: Record<string, unknown>) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/media`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
}

export async function updateAdminMedia(
  id: number | string,
  data: Record<string, unknown>,
) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/media/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  );
}

export async function deleteAdminMedia(id: number | string) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/media/${id}`,
    {
      method: 'DELETE',
    },
  );
}

export async function setAdminMediaFeatured(ids: number[]) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/media/featured`,
    {
      method: 'POST',
      body: JSON.stringify({ ids }),
    },
  );
}

// Admin quote management APIs
export interface Quote {
  id: number;
  text: string;
  author?: string | null;
  date: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface QuoteListResponse {
  data: Quote[];
  total: number;
  page: number;
  limit: number;
}

export interface QuotePayload {
  text: string;
  author?: string | null;
  date?: string;
}

function normalizeQuoteList(
  response: Quote[] | QuoteListResponse | null | undefined,
  fallback: { page: number; limit: number },
): QuoteListResponse {
  if (!response) {
    return { data: [], total: 0, page: fallback.page, limit: fallback.limit };
  }

  if (Array.isArray(response)) {
    return {
      data: response,
      total: response.length,
      page: fallback.page,
      limit: fallback.limit,
    };
  }

  return {
    data: response.data ?? [],
    total: response.total ?? response.data?.length ?? 0,
    page: response.page ?? fallback.page,
    limit: response.limit ?? fallback.limit,
  };
}

export async function fetchAdminQuotes(params: { page?: number; limit?: number } = {}) {
  const search = new URLSearchParams(
    Object.entries(params).reduce<Record<string, string>>((acc, [k, v]) => {
      if (v !== undefined) acc[k] = String(v);
      return acc;
    }, {}),
  );
  const query = search.toString();
  const base = `${API_BASE_URL}/api/admin/quote`;
  const response = await apiFetch<Quote[] | QuoteListResponse | null>(
    query ? `${base}?${query}` : base,
  );

  return normalizeQuoteList(response, {
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  });
}

export async function searchAdminQuotes(q: string, limit?: number) {
  const params = new URLSearchParams({ q });
  if (limit !== undefined) params.set('limit', String(limit));
  return apiFetch<Quote[]>(
    `${API_BASE_URL}/api/admin/quote/search?${params.toString()}`,
  );
}

export async function fetchAdminQuote(id: number | string) {
  return apiFetch<Quote>(`${API_BASE_URL}/api/admin/quote/${id}`);
}

export interface QuoteStats {
  viewCount: number;
}

export async function fetchAdminQuoteStats(id?: number | string) {
  const url = id
    ? `${API_BASE_URL}/api/admin/quote/stats?id=${id}`
    : `${API_BASE_URL}/api/admin/quote/stats`;
  return apiFetch<QuoteStats>(url);
}

export interface PublicQuote {
  text: string;
  author?: string | null;
  date: string;
}

export async function fetchQuoteOfTheDay() {
  return apiFetch<PublicQuote>(`${API_BASE_URL}/api/quote/today`);
}

export async function fetchRandomQuote() {
  return apiFetch<PublicQuote>(`${API_BASE_URL}/api/quote/random`);
}

export async function createAdminQuote(data: QuotePayload) {
  return apiFetch<Quote>(`${API_BASE_URL}/api/admin/quote`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminQuote(id: number | string, data: Partial<QuotePayload>) {
  return apiFetch(`${API_BASE_URL}/api/admin/quote/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminQuote(id: number | string) {
  return apiFetch(`${API_BASE_URL}/api/admin/quote/${id}`, {
    method: 'DELETE',
  });
}

export interface AuditLog {
  adminId: number;
  action: string;
  timestamp: string;
}

export async function fetchAdminAuditLogs() {
  return apiFetch<AuditLog[]>(
    `${API_BASE_URL}/api/admin/audit-logs`,
  );
}

export interface ReadingNote {
  id: number;
  title: string;
  content: string;
  pinned: boolean;
  order: number;
  createdAt: string;
}

export async function fetchAdminReadingNotes() {
  return apiFetch<ReadingNote[]>(`${API_BASE_URL}/api/admin/articles`);
}

export async function searchAdminReadingNotes(query: string) {
  return apiFetch<ReadingNote[]>(
    `${API_BASE_URL}/api/admin/articles/search?q=${encodeURIComponent(query)}`,
  );
}

export async function getAdminReadingNote(id: number | string) {
  return apiFetch<ReadingNote>(`${API_BASE_URL}/api/admin/articles/${id}`);
}

export interface ReadingNotePayload {
  title: string;
  content: string;
}

export async function createAdminReadingNote(data: ReadingNotePayload) {
  return apiFetch<{ id: number }>(`${API_BASE_URL}/api/admin/articles`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminReadingNote(id: number | string, data: ReadingNotePayload) {
  return apiFetch(`${API_BASE_URL}/api/admin/articles/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function updateAdminReadingNotePin(
  id: number | string,
  data: { pinned?: boolean; order?: number },
) {
  return apiFetch(`${API_BASE_URL}/api/admin/articles/${id}/pin`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminReadingNote(id: number | string) {
  return apiFetch(
    `${API_BASE_URL}/api/admin/articles/${id}`,
    {
      method: 'DELETE',
    },
  );
}

export async function searchDonations(query: string) {
  return apiFetch(`${API_BASE_URL}/api/devotee/search/donation?q=${encodeURIComponent(query)}`);
}

export async function downloadDonationReceipt(id: number | string) {
  const response = await fetch(`${API_BASE_URL}/api/devotee/donation/receipt/${id}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to download receipt');
  }
  return response.blob();
}

// Darshan APIs
export type DarshanType = 'Sringar' | 'Mangala' | 'Festival';

export interface DarshanMedia {
  id: number;
  type: DarshanType;
  date: string; // YYYY-MM-DD
  mediaType: 'image' | 'video';
  url: string;
  title?: string;
  order?: number;
}

export type DarshanMediaCreate = Omit<DarshanMedia, 'id'>;

export async function fetchAdminDarshan(params: { type: DarshanType; date: string }) {
  const search = new URLSearchParams({ type: params.type, date: params.date });
  return apiFetch<DarshanMedia[]>(`${API_BASE_URL}/api/admin/darshan?${search.toString()}`);
}

export async function createAdminDarshan(data: DarshanMediaCreate) {
  return apiFetch<{ id: number }>(`${API_BASE_URL}/api/admin/darshan`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminDarshan(id: number | string, data: Partial<DarshanMediaCreate>) {
  return apiFetch(`${API_BASE_URL}/api/admin/darshan/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminDarshan(id: number | string) {
  return apiFetch(`${API_BASE_URL}/api/admin/darshan/${id}`, { method: 'DELETE' });
}

// Public darshan fetch (no auth required)
export async function fetchDarshanByType(
  type: DarshanType,
  params: { date?: string; days?: number } = {},
) {
  const search = new URLSearchParams();
  if (params.date) search.set('date', params.date);
  if (params.days !== undefined) search.set('days', String(params.days));
  const query = search.toString();
  const base = `${API_BASE_URL}/api/darshan/${encodeURIComponent(type)}`;
  return apiFetch<DarshanMedia[]>(query ? `${base}?${query}` : base);
}

export async function uploadUserPhoto(file: File, onProgress?: (p: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}/api/user/media/upload`);
    xhr.withCredentials = true;
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300 && data?.url) {
          resolve(data.url);
        } else {
          reject(new Error(data?.error || 'Upload failed'));
        }
      } catch (err) {
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(formData);
  });
}

// Leaderboard APIs
export async function fetchLeaderboardUserMonthly() {
  return apiFetch(`${API_BASE_URL}/api/devotee/leaderboard/user/monthly`);
}

export async function fetchLeaderboardUserQuarterly() {
  return apiFetch(`${API_BASE_URL}/api/devotee/leaderboard/user/quarterly`);
}

export async function fetchLeaderboardUserYearly() {
  return apiFetch(`${API_BASE_URL}/api/devotee/leaderboard/user/yearly`);
}

export async function fetchLeaderboardEventMonthly(eventId: number) {
  return apiFetch(`${API_BASE_URL}/api/devotee/leaderboard/event/monthly/${eventId}`);
}

export async function fetchLeaderboardEventQuarterly(eventId: number) {
  return apiFetch(`${API_BASE_URL}/api/devotee/leaderboard/event/quarterly/${eventId}`);
}

export async function fetchLeaderboardEventYearly(eventId: number) {
  return apiFetch(`${API_BASE_URL}/api/devotee/leaderboard/event/yearly/${eventId}`);
}

// Campaign APIs (restored)
export async function createCampaign(eventId: number) {
  return apiFetch(`${API_BASE_URL}/api/devotee/campaign`, {
    method: 'POST',
    body: JSON.stringify({ eventId }),
  });
}

export async function fetchCampaigns() {
  return apiFetch(`${API_BASE_URL}/api/devotee/campaign`);
}

// Congregation Admin APIs
export async function fetchCongregationMe() {
  return apiFetch<CongregationMembership>(`${API_BASE_URL}/api/congregation/me`);
}

export async function checkCongregationAccess() {
  return apiFetch<{
    canAccess: boolean;
    isAdmin: boolean;
    isFacilitator: boolean;
    facilitatorBatches: number[];
    member: import('../types/congregation').CongregationMember;
    roles: import('../types/congregation').CongregationRoleAssignment[];
  }>(`${API_BASE_URL}/api/congregation/access-check`);
}

export async function fetchCongregationMasters() {
  return apiFetch<CongregationMastersPayload>(`${API_BASE_URL}/api/public/congregation/masters`);
}

export interface CongregationAdminFilter {
  centerId?: number | string;
  status?: CongregationApplicationStatus;
}

function buildCongregationQuery(params?: CongregationAdminFilter) {
  const searchParams = new URLSearchParams();
  if (params?.centerId) {
    searchParams.set('centerId', String(params.centerId));
  }
  if (params?.status) {
    searchParams.set('status', params.status);
  }
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

export async function fetchCongregationAdminApplications(params?: CongregationAdminFilter) {
  const query = buildCongregationQuery(params);
  return apiFetch<CongregationAdminApplicationsResponse>(
    `${API_BASE_URL}/api/congregation/admin/applications${query}`,
  );
}

export async function fetchCongregationAdminPipeline(params?: CongregationAdminFilter) {
  const query = buildCongregationQuery(params);
  return apiFetch<CongregationAdminPipelineResponse>(
    `${API_BASE_URL}/api/congregation/admin/reports/applications/pipeline${query}`,
  );
}

export interface CongregationAdminActivityFilter {
  limit?: number;
  cursor?: string | null;
}

function buildCongregationActivityQuery(params?: CongregationAdminActivityFilter) {
  if (!params) {
    return '';
  }
  const searchParams = new URLSearchParams();
  if (typeof params.limit === 'number' && !Number.isNaN(params.limit)) {
    searchParams.set('limit', String(params.limit));
  }
  if (params.cursor) {
    searchParams.set('cursor', params.cursor);
  }
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

export async function fetchCongregationAdminActivity(
  params?: CongregationAdminActivityFilter,
) {
  const query = buildCongregationActivityQuery(params);
  return apiFetch<CongregationAdminActivityResponse>(
    `${API_BASE_URL}/api/congregation/admin/activity${query}`,
  );
}

export async function createCongregationCenter(payload: CongregationCenterInput) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/centers`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCongregationCenter(id: number, payload: CongregationCenterInput) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/centers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function createCongregationCourse(payload: CongregationCourseInput) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/courses`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCongregationCourse(id: number, payload: CongregationCourseInput) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/courses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function createCongregationBook(payload: CongregationBookInput) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/books`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCongregationBook(id: number, payload: CongregationBookInput) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/books/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function createCongregationSkill(payload: CongregationSkillInput) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/skills`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Batch Management API
export async function listCongregationBatches(): Promise<{ batches: CongregationBatch[] }> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches`);
}

export async function getCongregationBatch(id: number): Promise<CongregationBatch> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches/${id}`);
}

export async function createCongregationBatch(payload: CongregationBatchInput): Promise<CongregationBatch> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCongregationBatch(id: number, payload: CongregationBatchInput): Promise<CongregationBatch> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function updateCongregationBatchStatus(id: number, status: CongregationBatchStatus): Promise<CongregationBatch> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export async function deleteCongregationBatch(id: number): Promise<void> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches/${id}`, {
    method: 'DELETE',
  });
}

// Batch Enrollment API
export async function listBatchEnrollments(batchId: number): Promise<{ enrollments: CongregationBatchEnrollment[] }> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches/${batchId}/enrollments`);
}

export async function enrollMemberInBatch(batchId: number, payload: CongregationBatchEnrollmentInput): Promise<CongregationBatchEnrollment> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches/${batchId}/enrollments`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateBatchEnrollmentStatus(
  batchId: number,
  enrollmentId: number,
  status: CongregationBatchEnrollmentStatus,
): Promise<CongregationBatchEnrollment> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches/${batchId}/enrollments/${enrollmentId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export async function deleteBatchEnrollment(batchId: number, enrollmentId: number): Promise<void> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches/${batchId}/enrollments/${enrollmentId}`, {
    method: 'DELETE',
  });
}

export async function updateCongregationSkill(id: number, payload: CongregationSkillInput) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/skills/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function createCongregationSpiritualMaster(
  payload: CongregationSpiritualMasterInput,
) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/spiritual-masters`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCongregationSpiritualMaster(
  id: number,
  payload: CongregationSpiritualMasterInput,
) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/spiritual-masters/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function createCongregationSpiritualGuide(
  payload: CongregationSpiritualGuideInput,
) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/spiritual-guides`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCongregationSpiritualGuide(
  id: number,
  payload: CongregationSpiritualGuideInput,
) {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/spiritual-guides/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function fetchCongregationApplication(id: number) {
  const response = await apiFetch<any>(
    `${API_BASE_URL}/api/congregation/applications/${id}`,
  );
  
  // Transform API response to match frontend types
  if (response?.events) {
    response.events = response.events.map((event: any) => ({
      ...event,
      type: event.eventType || event.type,
      actorName: event.actorName || null,
    }));
  }
  
  return response as CongregationApplicationDetail;
}

export async function assignCongregationApplication(
  id: number,
  payload: CongregationAdminAssignInput,
) {
  return apiFetch<{ application: CongregationApplication }>(
    `${API_BASE_URL}/api/congregation/admin/applications/${id}/assign`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function overrideCongregationApplication(
  id: number,
  payload: CongregationAdminOverrideInput,
) {
  return apiFetch<{ application: CongregationApplication }>(
    `${API_BASE_URL}/api/congregation/admin/applications/${id}/override`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function createCongregationMentorDevotee(
  payload: CongregationMentorDevoteeCreate,
) {
  return apiFetch<{ application: CongregationApplication }>(
    `${API_BASE_URL}/api/congregation/mentor/devotees`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function listMentorAssignedDevotees() {
  const response = await apiFetch<{ devotees: any[] }>(
    `${API_BASE_URL}/api/congregation/mentor/sadhana/devotees`,
  );
  // Map backend DevoteeWithStatus to CongregationMember-like structure
  return {
    devotees: (response.devotees || []).map((devotee: any) => ({
      id: devotee.memberId, // Use memberId, not id (id is userId)
      displayName: devotee.name || devotee.displayName,
      email: devotee.email || '',
      createdAt: new Date().toISOString(), // Dummy value for required field
    })) as CongregationMember[],
  };
}

export async function lookupCongregationMemberByUserId(userId: number) {
  return apiFetch<{ memberId: number; member: CongregationMemberProfile }>(
    `${API_BASE_URL}/api/congregation/admin/members/lookup`,
    {
      method: 'POST',
      body: JSON.stringify({ userId }),
    },
  );
}

export async function assignCongregationRole(
  payload: CongregationRoleAssignmentInput,
) {
  return apiFetch<{ role: CongregationRoleAssignment }>(
    `${API_BASE_URL}/api/congregation/admin/roles`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function revokeCongregationRole(id: number) {
  return apiFetch(
    `${API_BASE_URL}/api/congregation/admin/roles/${id}`,
    {
      method: 'DELETE',
    },
  );
}

export async function createCongregationMemberFromUser(userId: number) {
  return apiFetch<{ member: CongregationMemberProfile }>(
    `${API_BASE_URL}/api/congregation/admin/members/from-user`,
    {
      method: 'POST',
      body: JSON.stringify({ userId }),
    },
  );
}

export async function createCongregationMemberWithProfile(payload: {
  userId: number;
  displayName: string;
  legalName: string;
  phone: string;
  email: string;
  occupation: string;
  city: string;
  state: string;
  country: string;
  gender: string;
  maritalStatus: string;
  spiritualMasterId?: number | null;
  whatsappOptIn: boolean;
}) {
  return apiFetch<{ member: CongregationMember }>(
    `${API_BASE_URL}/api/congregation/admin/members/with-profile`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export interface CongregationMemberSearchOptions {
  centerId?: string;
  limit?: number;
}

export async function searchCongregationMembers(
  query: string,
  options: CongregationMemberSearchOptions = {},
) {
  const params = new URLSearchParams({ q: query });
  if (options.centerId) {
    params.set('centerId', options.centerId);
  }
  if (options.limit) {
    params.set('limit', String(options.limit));
  }
  return apiFetch<{ results: CongregationMemberSearchResult[] }>(
    `${API_BASE_URL}/api/congregation/admin/members/search?${params.toString()}`,
  );
}

export async function fetchAllAdminMembers(options: CongregationMemberSearchOptions = {}) {
  const params = new URLSearchParams();
  if (options.limit) {
    params.set('limit', String(options.limit));
  }
  const query = params.toString();
  return apiFetch<{ results: CongregationMemberSearchResult[] }>(
    `${API_BASE_URL}/api/congregation/admin/members${query ? `?${query}` : ''}`,
  );
}

export async function fetchAdminMembersWithActivityStatus() {
  const response = await apiFetch<{ results: Array<any> }>(
    `${API_BASE_URL}/api/congregation/admin/members`,
  );
  
  // Map the backend response structure to expected frontend structure
  return {
    devotees: (response.results || []).map((r: any) => ({
      id: r.member?.id,
      memberId: r.member?.id,
      name: r.member?.displayName || r.member?.legalName,
      email: r.member?.email,
      phoneNumber: r.member?.phone,
      status: 'active', // Backend doesn't return status in this endpoint
      membershipStatus: r.member?.status,
      lastSubmissionAt: null, // Not available from this endpoint
    })),
  };
}

export async function fetchCongregationMemberProfile(memberId?: number) {
  const params = new URLSearchParams();
  if (memberId) {
    params.set('memberId', String(memberId));
  }
  const query = params.toString();
  return apiFetch<CongregationMemberProfile>(
    `${API_BASE_URL}/api/congregation/profile${query ? `?${query}` : ''}`,
  );
}

export async function verifyCongregationDocument(
  documentId: number,
  payload: CongregationDocumentVerification,
) {
  return apiFetch<{ document: CongregationMemberDocument }>(
    `${API_BASE_URL}/api/congregation/documents/${documentId}/verify`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function verifyCongregationCourse(
  courseId: number,
  payload: CongregationCourseVerification,
) {
  return apiFetch<{ course: CongregationMemberCourse }>(
    `${API_BASE_URL}/api/congregation/courses/${courseId}/verify`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function updateCongregationChanting(
  chantingId: number,
  payload: CongregationChantingInput,
) {
  return apiFetch<{ chanting: CongregationChantingRecord }>(
    `${API_BASE_URL}/api/congregation/chanting/${chantingId}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

export async function updateAdminMemberPersonal(
  memberId: number,
  payload: any,
) {
  return apiFetch<{ member: CongregationMember }>(
    `${API_BASE_URL}/api/congregation/admin/members/personal?memberId=${memberId}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

export interface CongregationReportFilter {
  windowDays?: number;
  limit?: number;
  centerId?: number | string;
}

function buildReportQuery(params?: CongregationReportFilter) {
  const search = new URLSearchParams();
  if (params?.windowDays !== undefined) {
    search.set('windowDays', String(params.windowDays));
  }
  if (params?.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  if (params?.centerId !== undefined) {
    search.set('centerId', String(params.centerId));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export async function fetchCongregationBirthdays(params?: CongregationReportFilter) {
  const query = buildReportQuery(params);
  return apiFetch<{ birthdays: CongregationBirthdayReport[] }>(
    `${API_BASE_URL}/api/congregation/reports/birthdays${query}`,
  );
}

export async function fetchCongregationAnniversaries(params?: CongregationReportFilter) {
  const query = buildReportQuery(params);
  return apiFetch<{ anniversaries: CongregationAnniversaryReport[] }>(
    `${API_BASE_URL}/api/congregation/reports/anniversaries${query}`,
  );
}

export async function createCongregationHousehold(payload: CongregationHouseholdInput) {
  return apiFetch<{ household: CongregationHousehold }>(
    `${API_BASE_URL}/api/congregation/admin/households`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function updateCongregationHousehold(
  id: number,
  payload: Partial<CongregationHouseholdInput>,
) {
  return apiFetch<{ household: CongregationHousehold }>(
    `${API_BASE_URL}/api/congregation/admin/households/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

export async function addCongregationHouseholdMember(
  householdId: number,
  payload: CongregationHouseholdMemberInput,
) {
  return apiFetch<{ member: CongregationHouseholdMember }>(
    `${API_BASE_URL}/api/congregation/admin/households/${householdId}/members`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function updateCongregationHouseholdMember(
  memberId: number,
  payload: CongregationHouseholdMemberUpdateInput,
) {
  return apiFetch<{ member: CongregationHouseholdMember }>(
    `${API_BASE_URL}/api/congregation/admin/household-members/${memberId}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

export interface CongregationHouseholdMemberRemovalOptions {
  justification?: string;
}

export async function removeCongregationHouseholdMember(
  memberId: number,
  options: CongregationHouseholdMemberRemovalOptions = {},
) {
  const search = new URLSearchParams();
  if (options.justification) {
    search.set('justification', options.justification);
  }
  const query = search.toString();
  return apiFetch(
    `${API_BASE_URL}/api/congregation/admin/household-members/${memberId}${
      query ? `?${query}` : ''
    }`,
    {
      method: 'DELETE',
    },
  );
}

export async function setCongregationHouseholdHead(
  householdId: number,
  payload: CongregationHouseholdHeadInput,
) {
  return apiFetch<{ household: CongregationHousehold }>(
    `${API_BASE_URL}/api/congregation/admin/households/${householdId}/head`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function setCongregationHouseholdLabels(
  householdId: number,
  payload: CongregationHouseholdLabelAssignmentInput,
) {
  return apiFetch<{ household: CongregationHousehold }>(
    `${API_BASE_URL}/api/congregation/admin/households/${householdId}/labels`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchCongregationHouseholdLabels() {
  return apiFetch<{ labels: CongregationHouseholdLabel[] }>(
    `${API_BASE_URL}/api/congregation/admin/household-labels`,
  );
}

export async function createCongregationHouseholdLabel(
  payload: CongregationHouseholdLabelInput,
) {
  return apiFetch<{ label: CongregationHouseholdLabel }>(
    `${API_BASE_URL}/api/congregation/admin/household-labels`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function updateCongregationHouseholdLabel(
  id: number,
  payload: Partial<CongregationHouseholdLabelInput>,
) {
  return apiFetch<{ label: CongregationHouseholdLabel }>(
    `${API_BASE_URL}/api/congregation/admin/household-labels/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

export interface CongregationHouseholdRosterFilter {
  centerId?: number | string;
  search?: string;
  labelIds?: Array<number | string>;
  page?: number;
  pageSize?: number;
}

export async function fetchCongregationHouseholdRoster(
  params: CongregationHouseholdRosterFilter = {},
) {
  const search = new URLSearchParams();
  if (params.centerId !== undefined) {
    search.set('centerId', String(params.centerId));
  }
  if (params.search) {
    search.set('search', params.search);
  }
  if (params.labelIds && params.labelIds.length > 0) {
    search.set('labelIds', params.labelIds.map(String).join(','));
  }
  if (params.page !== undefined) {
    search.set('page', String(params.page));
  }
  if (params.pageSize !== undefined) {
    search.set('pageSize', String(params.pageSize));
  }
  const query = search.toString();
  return apiFetch<{ households: CongregationHouseholdRosterEntry[] }>(
    `${API_BASE_URL}/api/congregation/admin/households/roster${query ? `?${query}` : ''}`,
  );
}

export async function exportCongregationHouseholdRoster(
  params: CongregationHouseholdRosterFilter = {},
) {
  const search = new URLSearchParams();
  if (params.centerId !== undefined && params.centerId !== '') {
    search.set('centerId', String(params.centerId));
  }
  if (params.search) {
    search.set('search', params.search);
  }
  if (params.labelIds && params.labelIds.length > 0) {
    search.set('labelIds', params.labelIds.map(String).join(','));
  }
  const query = search.toString();
  const response = await fetch(
    `${API_BASE_URL}/api/congregation/admin/households/export${query ? `?${query}` : ''}`,
    {
      credentials: 'include',
    },
  );

  if (!response.ok) {
    let message = 'Unable to export households';
    let data: unknown = null;
    try {
      const raw = await response.text();
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          data = parsed;
          if (parsed && typeof parsed === 'object' && 'message' in parsed) {
            message = String((parsed as { message?: unknown }).message ?? message);
          }
        } catch {
          data = raw;
          message = raw;
        }
      }
    } catch {
      // ignore error while parsing body
    }
    throw new ApiError(message, response.status, data);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const timestampSegment = new Date().toISOString().replace(/[:.]/g, '-');
  const sanitizeSegment = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 24);
  const metadataSegments: string[] = [];
  if (params.centerId !== undefined && params.centerId !== '' && params.centerId !== null) {
    metadataSegments.push(`center-${sanitizeSegment(String(params.centerId)) || 'all'}`);
  }
  if (params.search) {
    const sanitizedSearch = sanitizeSegment(params.search);
    metadataSegments.push(`search-${sanitizedSearch || 'query'}`);
  }
  if (params.labelIds && params.labelIds.length > 0) {
    metadataSegments.push(`labels-${params.labelIds.map((id) => sanitizeSegment(String(id))).join('-')}`);
  }
  const metadataSuffix = metadataSegments.length ? `_${metadataSegments.join('_')}` : '';
  const fallbackFilename = `congregation-households${metadataSuffix}_${timestampSegment}.csv`;
  let filename = fallbackFilename;
  if (disposition) {
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    if (match && match[1]) {
      const base = match[1];
      if (metadataSuffix) {
        const dotIndex = base.lastIndexOf('.');
        if (dotIndex > 0) {
          filename = `${base.slice(0, dotIndex)}${metadataSuffix}${base.slice(dotIndex)}`;
        } else {
          filename = `${base}${metadataSuffix}`;
        }
      } else {
        filename = base;
      }
    }
  }

  return { blob, filename };
}

export async function fetchCongregationEligibilitySummary() {
  return apiFetch<{ rules: CongregationEligibilitySummary[] }>(
    `${API_BASE_URL}/api/congregation/admin/reports/eligibility`,
  );
}

export async function fetchCongregationMentorCohorts() {
  return apiFetch<{ cohorts: CongregationMentorCohortSummary[] }>(
    `${API_BASE_URL}/api/congregation/admin/reports/mentor-cohorts`,
  );
}

export async function fetchCongregationChantingProgress() {
  return apiFetch<{ buckets: CongregationChantingProgressRow[] }>(
    `${API_BASE_URL}/api/congregation/admin/reports/chanting-progress`,
  );
}

export async function fetchCongregationCourseHeatmap() {
  return apiFetch<{ courses: CongregationCourseHeatmapRow[] }>(
    `${API_BASE_URL}/api/congregation/admin/reports/course-heatmap`,
  );
}

export async function fetchCongregationEligibilityRules() {
  return apiFetch<{ rules: CongregationEligibilityRule[] }>(
    `${API_BASE_URL}/api/congregation/admin/eligibility/rules`,
  );
}

export async function createCongregationEligibilityRule(
  payload: CongregationEligibilityRuleInput,
) {
  return apiFetch<{ rule: CongregationEligibilityRule }>(
    `${API_BASE_URL}/api/congregation/admin/eligibility/rules`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function updateCongregationEligibilityRule(
  id: number,
  payload: CongregationEligibilityRuleInput,
) {
  return apiFetch<{ rule: CongregationEligibilityRule }>(
    `${API_BASE_URL}/api/congregation/admin/eligibility/rules/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

export async function evaluateCongregationEligibilityRule(ruleId: number, memberId: number) {
  const query = new URLSearchParams({ memberId: String(memberId) });
  return apiFetch<{ evaluation: CongregationEligibilityEvaluation }>(
    `${API_BASE_URL}/api/congregation/admin/eligibility/rules/${ruleId}/evaluate?${query.toString()}`,
    {
      method: 'POST',
    },
  );
}

export async function fetchCongregationImports() {
  return apiFetch<{ imports: CongregationImportJob[] }>(
    `${API_BASE_URL}/api/congregation/admin/imports`,
  );
}

export async function createCongregationImport(payload: CongregationImportRequest) {
  return apiFetch<{ import: CongregationImportJob }>(
    `${API_BASE_URL}/api/congregation/admin/imports`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchCongregationImportRows(importId: number) {
  return apiFetch<{ rows: CongregationImportJobRow[] }>(
    `${API_BASE_URL}/api/congregation/admin/imports/${importId}/rows`,
  );
}

export async function fetchCongregationBackups() {
  return apiFetch<{ backups: CongregationBackupRun[] }>(
    `${API_BASE_URL}/api/congregation/admin/backups`,
  );
}

export async function createCongregationBackup(payload: CongregationBackupRequest) {
  return apiFetch<{ backup: CongregationBackupRun }>(
    `${API_BASE_URL}/api/congregation/admin/backups`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function updateCongregationBackup(
  backupId: number,
  payload: CongregationBackupUpdate,
) {
  return apiFetch<{ backup: CongregationBackupRun }>(
    `${API_BASE_URL}/api/congregation/admin/backups/${backupId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchCongregationAlerts() {
  return apiFetch<{ alerts: CongregationMonitoringAlert[] }>(
    `${API_BASE_URL}/api/congregation/admin/alerts`,
  );
}

export async function createCongregationAlert(payload: CongregationAlertRequest) {
  return apiFetch<{ alert: CongregationMonitoringAlert }>(
    `${API_BASE_URL}/api/congregation/admin/alerts`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function updateCongregationAlert(
  alertId: number,
  payload: CongregationAlertRequest,
) {
  return apiFetch<{ alert: CongregationMonitoringAlert }>(
    `${API_BASE_URL}/api/congregation/admin/alerts/${alertId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchCongregationAccessReviews() {
  return apiFetch<{ accessReviews: CongregationAccessReview[] }>(
    `${API_BASE_URL}/api/congregation/admin/access-reviews`,
  );
}

export async function createCongregationAccessReview(
  payload: CongregationAccessReviewRequest,
) {
  return apiFetch<{ accessReview: CongregationAccessReview }>(
    `${API_BASE_URL}/api/congregation/admin/access-reviews`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

const buildSadhanaQueryString = (params: Record<string, unknown>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (typeof value === 'boolean') {
      search.set(key, value ? 'true' : 'false');
    } else {
      search.set(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

export interface FetchSadhanaTemplatesOptions {
  includeItems?: boolean;
}

export interface SadhanaTemplateAssignmentQuery {
  memberId?: number;
}

export interface SadhanaWeeksQuery {
  memberId?: number;
  limit?: number;
  before?: string;
}

export interface SadhanaWeekDetailQuery {
  memberId?: number;
}

export interface SadhanaCurrentWeekQuery {
  memberId?: number;
  date?: string;
}

export interface SadhanaWeekSubmitOptions {
  memberId?: number;
}

export interface SadhanaWeekMentorCommentOptions {
  memberId?: number;
}

export interface SadhanaMigrationStatusQuery {
  memberId?: number;
}

export interface SadhanaMigrationRunQuery {
  memberId?: number;
  limit?: number;
  before?: string | null;
}

export interface SadhanaReseedOptions {
  memberId: number;
}

export interface ChantingPrescriptionQuery {
  memberId?: number;
}

export async function fetchSadhanaTemplates(
  options: FetchSadhanaTemplatesOptions = {},
): Promise<SadhanaTemplateListResponse> {
  const query = buildSadhanaQueryString({ includeItems: options.includeItems });
  return apiFetch<SadhanaTemplateListResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/templates${query}`,
  );
}

export async function fetchSadhanaDefaultTemplate(): Promise<SadhanaTemplateResponse> {
  return apiFetch<SadhanaTemplateResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/templates/default`,
  );
}

export async function fetchSadhanaCurrentAssignment(
  options: SadhanaTemplateAssignmentQuery = {},
): Promise<SadhanaCurrentAssignmentResponse> {
  const query = buildSadhanaQueryString({ memberId: options.memberId });
  return apiFetch<SadhanaCurrentAssignmentResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/templates/assignment${query}`,
  );
}

export async function fetchSadhanaAssignments(
  options: SadhanaTemplateAssignmentQuery = {},
): Promise<SadhanaAssignmentsResponse> {
  const query = buildSadhanaQueryString({ memberId: options.memberId });
  return apiFetch<SadhanaAssignmentsResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/templates/assignments${query}`,
  );
}

export async function createSadhanaAssignment(
  payload: SadhanaTemplateAssignmentRequest,
  options: SadhanaTemplateAssignmentQuery = {},
): Promise<SadhanaAssignmentResponse> {
  const query = buildSadhanaQueryString({ memberId: options.memberId });
  return apiFetch<SadhanaAssignmentResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/templates/assignments${query}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchSadhanaWeeks(
  options: SadhanaWeeksQuery = {},
): Promise<SadhanaWeekListResponse> {
  const query = buildSadhanaQueryString({
    memberId: options.memberId,
    limit: options.limit,
    before: options.before,
  });
  return apiFetch<SadhanaWeekListResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/weeks${query}`,
  );
}

export async function fetchSadhanaCurrentWeek(
  options: SadhanaCurrentWeekQuery = {},
): Promise<SadhanaWeekDetailResponse> {
  const query = buildSadhanaQueryString({ memberId: options.memberId, date: options.date });
  return apiFetch<SadhanaWeekDetailResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/weeks/current${query}`,
  );
}

export async function fetchSadhanaWeek(
  weekId: number,
  options: SadhanaWeekDetailQuery = {},
): Promise<SadhanaWeekDetailResponse> {
  const query = buildSadhanaQueryString({ memberId: options.memberId });
  return apiFetch<SadhanaWeekDetailResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/weeks/${weekId}${query}`,
  );
}

export async function submitSadhanaWeek(
  weekId: number,
  options: SadhanaWeekSubmitOptions = {},
): Promise<SadhanaWeekSubmitResponse> {
  const query = buildSadhanaQueryString({ memberId: options.memberId });
  return apiFetch<SadhanaWeekSubmitResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/weeks/${weekId}/submit${query}`,
    {
      method: 'PUT',
    },
  );
}

export async function updateSadhanaWeekMentorComment(
  weekId: number,
  payload: SadhanaWeekMentorCommentRequest,
  options: SadhanaWeekMentorCommentOptions = {},
): Promise<SadhanaWeekMentorCommentResponse> {
  const query = buildSadhanaQueryString({ memberId: options.memberId });
  return apiFetch<SadhanaWeekMentorCommentResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/weeks/${weekId}/mentor-comment${query}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchSadhanaMigrationStatus(
  options: SadhanaMigrationStatusQuery = {},
): Promise<SadhanaMigrationStatusResponse> {
  const query = buildSadhanaQueryString({ memberId: options.memberId });
  return apiFetch<SadhanaMigrationStatusResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/migration/status${query}`,
  );
}

export async function fetchSadhanaMigrationRuns(
  options: SadhanaMigrationRunQuery = {},
): Promise<SadhanaMigrationRunListResponse> {
  const query = buildSadhanaQueryString({
    memberId: options.memberId,
    limit: options.limit,
    before: options.before,
  });
  return apiFetch<SadhanaMigrationRunListResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/migration/runs${query}`,
  );
}

export async function triggerSadhanaReseed(
  options: SadhanaReseedOptions,
  payload: SadhanaReseedRequest,
): Promise<SadhanaReseedResponse> {
  const query = buildSadhanaQueryString({ memberId: options.memberId });
  return apiFetch<SadhanaReseedResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/migration/reseed${query}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchChantingPrescriptions(
  options: ChantingPrescriptionQuery = {},
): Promise<ChantingPrescriptionListResponse> {
  const query = buildSadhanaQueryString({ memberId: options.memberId });
  return apiFetch<ChantingPrescriptionListResponse>(
    `${API_BASE_URL}/api/congregation/chanting${query}`,
  );
}

export async function createChantingPrescription(
  payload: ChantingPrescriptionCreateRequest,
  options: ChantingPrescriptionQuery = {},
): Promise<ChantingPrescriptionCreateResponse> {
  const query = buildSadhanaQueryString({ memberId: options.memberId });
  return apiFetch<ChantingPrescriptionCreateResponse>(
    `${API_BASE_URL}/api/congregation/chanting${query}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}
// ==================== Batch Posts ====================

export async function listBatchPosts(
  batchId: number,
  memberId?: number,
): Promise<CongregationBatchPostWithDetails[]> {
  const url = new URL(`${API_BASE_URL}/api/congregation/admin/batches/${batchId}/posts`);
  if (memberId) url.searchParams.append('memberId', memberId.toString());
  return apiFetch<CongregationBatchPostWithDetails[]>(url.toString());
}

export async function createBatchPost(
  batchId: number,
  payload: CongregationBatchPostInput,
): Promise<CongregationBatchPostWithDetails> {
  return apiFetch<CongregationBatchPostWithDetails>(
    `${API_BASE_URL}/api/congregation/admin/batches/${batchId}/posts`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function getBatchPost(
  postId: number,
  memberId?: number,
): Promise<CongregationBatchPostWithDetails> {
  const url = new URL(`${API_BASE_URL}/api/congregation/admin/posts/${postId}`);
  if (memberId) url.searchParams.append('memberId', memberId.toString());
  return apiFetch<CongregationBatchPostWithDetails>(url.toString());
}

export async function updateBatchPost(
  postId: number,
  content: string,
  isPinned: boolean,
): Promise<CongregationBatchPostWithDetails> {
  return apiFetch<CongregationBatchPostWithDetails>(
    `${API_BASE_URL}/api/congregation/admin/posts/${postId}`,
    {
      method: 'PUT',
      body: JSON.stringify({ content, isPinned }),
    },
  );
}

export async function deleteBatchPost(postId: number): Promise<void> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/posts/${postId}`, {
    method: 'DELETE',
  });
}

export async function addPostReaction(
  postId: number,
  reactionType: BatchPostReactionType,
): Promise<CongregationBatchPostWithDetails> {
  return apiFetch<CongregationBatchPostWithDetails>(
    `${API_BASE_URL}/api/congregation/admin/posts/${postId}/reactions`,
    {
      method: 'POST',
      body: JSON.stringify({ reactionType }),
    },
  );
}

export async function removePostReaction(postId: number): Promise<CongregationBatchPostWithDetails> {
  return apiFetch<CongregationBatchPostWithDetails>(
    `${API_BASE_URL}/api/congregation/admin/posts/${postId}/reactions`,
    {
      method: 'DELETE',
    },
  );
}

export async function listPostReplies(postId: number): Promise<CongregationBatchPostReply[]> {
  return apiFetch<CongregationBatchPostReply[]>(
    `${API_BASE_URL}/api/congregation/admin/posts/${postId}/replies`,
  );
}

export async function createPostReply(
  postId: number,
  payload: CongregationBatchPostReplyInput,
): Promise<CongregationBatchPostReply> {
  return apiFetch<CongregationBatchPostReply>(
    `${API_BASE_URL}/api/congregation/admin/posts/${postId}/replies`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function deletePostReply(replyId: number): Promise<void> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/posts/replies/${replyId}`, {
    method: 'DELETE',
  });
}

export async function votePoll(postId: number, optionId: number): Promise<CongregationBatchPostWithDetails> {
  return apiFetch<CongregationBatchPostWithDetails>(
    `${API_BASE_URL}/api/congregation/admin/posts/${postId}/vote`,
    {
      method: 'POST',
      body: JSON.stringify({ optionId }),
    },
  );
}

// ========================================
// Book Reading Tracking API Functions
// ========================================

export interface CongregationBookReading {
  id: number;
  memberId: number;
  memberName?: string;
  bookId: number;
  bookTitle?: string;
  bookAuthor?: string | null;
  status: 'READING' | 'COMPLETED' | 'ABANDONED';
  startDate: string;
  finishDate?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CongregationBookRecommendation {
  id: number;
  bookId: number;
  bookTitle?: string;
  bookAuthor?: string | null;
  recommendedBy: number;
  recommendedByName?: string;
  targetType: 'BATCH' | 'MEMBER';
  targetId: number;
  targetName?: string;
  message?: string | null;
  priority?: number | null;
  createdAt: string;
}

export interface CongregationBookRecommendationInput {
  bookId: number;
  targetType: 'BATCH' | 'MEMBER';
  targetId: number;
  message?: string | null;
  priority?: number | null;
}

export async function listBookReadingsByMember(
  memberId?: number,
): Promise<{ readings: CongregationBookReading[] }> {
  const url = new URL(`${API_BASE_URL}/api/congregation/admin/book-readings`);
  if (memberId) url.searchParams.append('memberId', memberId.toString());
  return apiFetch<{ readings: CongregationBookReading[] }>(url.toString());
}

export async function listBatchBookReadings(
  batchId: number,
): Promise<{ readings: CongregationBookReading[] }> {
  return apiFetch<{ readings: CongregationBookReading[] }>(
    `${API_BASE_URL}/api/congregation/admin/batches/${batchId}/book-readings`,
  );
}

export async function createBookRecommendation(
  input: CongregationBookRecommendationInput,
): Promise<{ recommendation: CongregationBookRecommendation }> {
  return apiFetch<{ recommendation: CongregationBookRecommendation }>(
    `${API_BASE_URL}/api/congregation/admin/book-recommendations`,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
}

export async function listBatchBookRecommendations(
  batchId: number,
): Promise<{ recommendations: CongregationBookRecommendation[] }> {
  return apiFetch<{ recommendations: CongregationBookRecommendation[] }>(
    `${API_BASE_URL}/api/congregation/admin/batches/${batchId}/book-recommendations`,
  );
}

export async function deleteBookRecommendation(id: number): Promise<void> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/book-recommendations/${id}`, {
    method: 'DELETE',
  });
}

export async function listAllBookReadings(): Promise<CongregationBookReading[]> {
  return apiFetch<CongregationBookReading[]>(
    `${API_BASE_URL}/api/congregation/admin/all-book-readings`,
  );
}

export async function listAllBookRecommendations(): Promise<CongregationBookRecommendation[]> {
  return apiFetch<CongregationBookRecommendation[]>(
    `${API_BASE_URL}/api/congregation/admin/all-book-recommendations`,
  );
}

// Admin Sadhana APIs - Allow admins to view sadhana of all devotees
export interface AdminAllDevoteesResponse {
  devotees: Array<{
    memberId: number;
    name: string;
    email?: string;
    status?: string;
  }>;
}

export async function listAdminAllDevotees(): Promise<AdminAllDevoteesResponse> {
  try {
    // Fetch all members using the admin members endpoint
    const result = await fetchAllAdminMembers({ limit: 500 });
    
    // Transform the response to match our expected format
    const devotees = (result.results || []).map((m: any) => ({
      memberId: m?.member?.id,
      name: m?.member?.displayName || (m?.member?.id ? `Member #${m.member.id}` : 'Member'),
      email: m?.member?.email || undefined,
      status: m?.member?.status || 'INACTIVE',
    })).filter((d: any) => d.memberId != null);
    
    return { devotees };
  } catch (error) {
    console.error('Failed to fetch admin devotees:', error);
    throw error;
  }
}

// Use mentor-style endpoint so we get the enriched WeeklySadhanaCard payload
// (includes isSubmitted, streak, daily averages, etc.)
export async function fetchAdminDevoteeSadhanaCard(
  memberId: number,
  weekStart?: string,
): Promise<any> {
  const params = new URLSearchParams();
  if (memberId) params.append('devoteeMemberId', String(memberId));
  if (weekStart) params.append('weekStartDate', weekStart);
  const query = params.toString();
  return apiFetch<any>(
    `${API_BASE_URL}/api/congregation/mentor/sadhana/devotee/card${query ? `?${query}` : ''}`,
  );
}

export async function fetchAdminDevoteeSadhanaCards(
  memberId: number,
  limit: number = 10,
  offset: number = 0,
): Promise<SadhanaWeekListResponse> {
  const params = new URLSearchParams();
  if (memberId) params.append('memberId', String(memberId));
  params.append('limit', Math.min(Math.max(limit, 1), 52).toString());
  params.append('offset', Math.max(offset, 0).toString());
  
  const query = params.toString();
  return apiFetch<SadhanaWeekListResponse>(
    `${API_BASE_URL}/api/congregation/sadhana/weekly/cards${query ? `?${query}` : ''}`,
  );
}

// ============================================================================
// Batch Attendance API
// ============================================================================

export async function listAllBatchAttendance(
  startDate?: string,
  endDate?: string,
  batchId?: number,
  mentorId?: number
): Promise<{ attendances: import('../types/congregation').BatchAttendance[] }> {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (batchId) params.append('batchId', batchId.toString());
  if (mentorId) params.append('mentorId', mentorId.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/attendance${query}`);
}

export async function getBatchAttendanceById(
  batchId: number,
  attendanceId: number
): Promise<import('../types/congregation').BatchAttendanceWithRecords> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches/${batchId}/attendance/${attendanceId}`);
}

export async function getMemberAttendanceHistory(
  memberId: number,
  startDate?: string,
  endDate?: string
): Promise<{ records: import('../types/congregation').BatchAttendanceRecord[] }> {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(`${API_BASE_URL}/api/congregation/members/${memberId}/attendance${query}`);
}
// Services
export async function getServiceTypes(): Promise<{ serviceTypes: import('../types/congregation').CongregationServiceType[] }> {
  return apiFetch(`${API_BASE_URL}/api/congregation/services/types`);
}

export async function createService(
  input: import('../types/congregation').CongregationServiceInput
): Promise<import('../types/congregation').CongregationService> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/services`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateService(
  serviceId: number,
  input: import('../types/congregation').CongregationServiceInput
): Promise<import('../types/congregation').CongregationService> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/services/${serviceId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deleteService(serviceId: number): Promise<{ message: string }> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/services/${serviceId}`, {
    method: 'DELETE',
  });
}

export async function createServiceType(
  name: string,
  description: string
): Promise<import('../types/congregation').CongregationServiceType> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/services/types`, {
    method: 'POST',
    body: JSON.stringify({ name, description }),
  });
}

export async function updateServiceType(
  id: number,
  name: string,
  description: string
): Promise<import('../types/congregation').CongregationServiceType> {
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/services/types/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name, description }),
  });
}

export async function listBatchServices(
  batchId: number,
  status?: string
): Promise<{ services: import('../types/congregation').CongregationService[] }> {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(`${API_BASE_URL}/api/congregation/admin/batches/${batchId}/services${query}`);
}

export async function assignFacilitator(
  payload: import('../types/congregation').CongregationFacilitatorInput,
) {
  return apiFetch<{ facilitator: import('../types/congregation').CongregationFacilitator }>(
    `${API_BASE_URL}/api/congregation/admin/facilitators`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function listFacilitatorsByBatch(
  batchId: number,
): Promise<{ facilitators: import('../types/congregation').CongregationFacilitator[] }> {
  return apiFetch(
    `${API_BASE_URL}/api/congregation/admin/batches/${batchId}/facilitators`,
  );
}

export async function revokeFacilitator(batchId: number, memberId: number) {
  return apiFetch(
    `${API_BASE_URL}/api/congregation/admin/batches/${batchId}/facilitators/${memberId}`,
    {
      method: 'DELETE',
    },
  );
}